import { useState, useCallback } from "react";
import { FileText, Send, Eye } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import { FilterBar } from "@/components/admin/shared/FilterBar";
import type { FilterConfig } from "@/components/admin/shared/FilterBar";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { useEInvoices, useSubmitEInvoice } from "@/hooks/admin/useEnterprise";
import type { EInvoice, EInvoiceStatus } from "@/lib/admin-api/enterprise";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<EInvoiceStatus, string> = {
  pending: "En attente",
  submitted: "Soumise",
  accepted: "Acceptée",
  rejected: "Rejetée",
};

const STATUS_COLORS: Record<EInvoiceStatus, string> = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  submitted: "bg-blue-100 text-blue-800 border-blue-200",
  accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

const FILTER_CONFIGS: readonly FilterConfig[] = [
  {
    key: "status",
    label: "Statut",
    type: "select",
    options: [
      { value: "pending", label: "En attente" },
      { value: "submitted", label: "Soumise" },
      { value: "accepted", label: "Acceptée" },
      { value: "rejected", label: "Rejetée" },
    ],
  },
  { key: "date_from", label: "Du", type: "date" },
  { key: "date_to", label: "Au", type: "date" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-SN");
}

function formatFCFA(n: number): string {
  return n.toLocaleString("fr-SN") + " FCFA";
}

// ---------------------------------------------------------------------------
// Invoice Detail Modal
// ---------------------------------------------------------------------------

interface InvoiceDetailModalProps {
  readonly invoice: EInvoice | null;
  readonly onClose: () => void;
}

function InvoiceDetailModal({ invoice, onClose }: InvoiceDetailModalProps) {
  if (!invoice) return null;

  return (
    <AdminModal
      open={Boolean(invoice)}
      onClose={onClose}
      title={`Facture ${invoice.invoice_number}`}
      size="md"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-stone-500">Client</p>
            <p className="mt-0.5 text-sm text-stone-900">{invoice.customer_name}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-stone-500">Commande</p>
            <p className="mt-0.5 text-sm text-stone-900">{invoice.order_ref}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-stone-500">NINEA</p>
            <p className="mt-0.5 font-mono text-sm text-stone-900">{invoice.ninea ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-stone-500">Réf. DGI</p>
            <p className="mt-0.5 font-mono text-sm text-stone-900">{invoice.dgi_ref ?? "—"}</p>
          </div>
        </div>

        <div className="rounded-lg border border-stone-100 bg-stone-50 p-4">
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-stone-600">Montant HT</span>
            <span className="text-sm font-medium text-stone-900">
              {formatFCFA(invoice.amount_ht_fcfa)}
            </span>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-stone-600">TVA</span>
            <span className="text-sm font-medium text-stone-900">
              {formatFCFA(invoice.tva_fcfa)}
            </span>
          </div>
          <div className="mt-1 flex items-center justify-between border-t border-stone-200 pt-2">
            <span className="text-sm font-semibold text-stone-900">Total TTC</span>
            <span className="text-base font-bold text-stone-900">
              {formatFCFA(invoice.amount_ttc_fcfa)}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-stone-500">Soumis le</p>
            <p className="text-sm text-stone-900">{formatDate(invoice.submitted_at)}</p>
          </div>
          <span
            className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${STATUS_COLORS[invoice.status]}`}
          >
            {STATUS_LABELS[invoice.status]}
          </span>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function EInvoice() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [selectedInvoice, setSelectedInvoice] = useState<EInvoice | null>(null);

  const params = {
    page,
    limit: 20,
    status: (filters.status as EInvoiceStatus) ?? undefined,
    date_from: (filters.date_from as string) ?? undefined,
    date_to: (filters.date_to as string) ?? undefined,
  };

  const { data, isLoading } = useEInvoices(params);
  const submitMutation = useSubmitEInvoice();

  const invoices = data?.invoices ?? [];
  const pagination = data?.pagination;

  const pendingCount = invoices.filter((i) => i.status === "pending").length;
  const submittedCount = invoices.filter((i) => i.status === "submitted").length;
  const acceptedCount = invoices.filter((i) => i.status === "accepted").length;
  const rejectedCount = invoices.filter((i) => i.status === "rejected").length;

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleSubmit = useCallback(
    async (id: string) => {
      if (!confirm("Soumettre cette facture à la DGI ?")) return;
      await submitMutation.mutateAsync(id);
    },
    [submitMutation],
  );

  const columns: readonly ColumnDef<EInvoice>[] = [
    {
      id: "invoice_number",
      header: "N° Facture",
      cell: (row) => (
        <span className="font-mono text-xs font-medium text-stone-900">
          {row.invoice_number}
        </span>
      ),
    },
    {
      id: "order_ref",
      header: "Commande",
      cell: (row) => (
        <span className="text-xs text-stone-600">{row.order_ref}</span>
      ),
    },
    {
      id: "customer_name",
      header: "Client",
      cell: (row) => (
        <span className="text-sm text-stone-900">{row.customer_name}</span>
      ),
    },
    {
      id: "ninea",
      header: "NINEA",
      cell: (row) => (
        <span className="font-mono text-xs text-stone-600">{row.ninea ?? "—"}</span>
      ),
    },
    {
      id: "amount_ttc_fcfa",
      header: "Montant TTC",
      cell: (row) => (
        <span className="text-sm font-medium text-stone-900">
          {formatFCFA(row.amount_ttc_fcfa)}
        </span>
      ),
    },
    {
      id: "status",
      header: "Statut",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[row.status]}`}
        >
          {STATUS_LABELS[row.status]}
        </span>
      ),
    },
    {
      id: "submitted_at",
      header: "Soumis le",
      cell: (row) => (
        <span className="text-xs text-stone-400">{formatDate(row.submitted_at)}</span>
      ),
    },
    {
      id: "dgi_ref",
      header: "Réf. DGI",
      cell: (row) => (
        <span className="font-mono text-xs text-stone-600">{row.dgi_ref ?? "—"}</span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row) => (
        <div className="flex items-center gap-1.5">
          {row.status === "pending" && (
            <button
              onClick={() => handleSubmit(row.id)}
              className="flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
              title="Soumettre à la DGI"
            >
              <Send className="h-3 w-3" />
              Soumettre DGI
            </button>
          )}
          <button
            onClick={() => setSelectedInvoice(row)}
            className="flex items-center gap-1 rounded-md border border-stone-200 bg-white px-2 py-1 text-xs font-medium text-stone-600 hover:bg-stone-50"
            title="Voir détail"
          >
            <Eye className="h-3 w-3" />
            Détail
          </button>
        </div>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Facture électronique
            </h1>
            <p className="text-sm text-stone-500">
              Conformité DGI Sénégal — Soumission des factures
            </p>
          </div>
        </div>

        {/* Legal banner */}
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <FileText className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">Facture électronique</span> — Conformité DGI
            Sénégal. Le NINEA est requis pour les factures B2B. Vérifiez les informations
            avant toute soumission.
          </p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard label="En attente" value={pendingCount} />
          <KPICard label="Soumises" value={submittedCount} />
          <KPICard label="Acceptées" value={acceptedCount} />
          <KPICard label="Rejetées" value={rejectedCount} />
        </div>

        {/* Filters */}
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <FilterBar filters={FILTER_CONFIGS} values={filters} onChange={handleFilterChange} />
        </div>

        {/* Table */}
        <AdminDataTable
          columns={columns}
          data={invoices}
          pagination={
            pagination
              ? { page: pagination.page, limit: pagination.limit, total: pagination.total }
              : undefined
          }
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="Aucune facture électronique trouvée."
        />
      </div>

      <InvoiceDetailModal
        invoice={selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
      />
    </AdminLayout>
  );
}
